import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, PLATFORM_ID} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserModel, ContactsModel, RequestParamModel, QuoteOrderSampleModel } from '@coremodels';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Constants } from 'src/app/admin-core/constants';
import { QuoteOrderSampleService } from 'src/app/admin-core/apiservices';
import {
  AuthService,
  HelperService,
  AlertService,
  SubscribersService,
  DebugLoggerService
} from 'src/app/admin-core/services';
import { isPlatformBrowser } from "@angular/common";
import { Observable } from 'rxjs';

/** The address check every row uses. */
const EMAIL_PATTERN = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/** console-v2's cap: up to 3 addresses in all - the contact's plus 2 added rows. */
const MAX_ADDED_EMAILS = 2;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-quote-invoice-export-modal',
  templateUrl: './quote-invoice-export-modal.component.html',
  // console-v2's Export dialog on the Pebbl shell. The legacy
  // quote-invoice-export-modal.component.css stays in the repo, unchanged:
  // TrackOrderExportModalComponent still loads it.
  styleUrls: ['../../components/pebbl-modal/pebbl-dialog.css', './quote-invoice-export-modal.pebbl.css']
})
export class QuoteInvoiceExportModalComponent implements OnInit {
  @Input() quoteModel: any;
  @Input() typeForDownload: any;
  @Input() isConsumableSummaryExportPopupOn: boolean = true;
  hasFormError = false;
  emailAddressObject = {
    email: '',
    isEmpty: false,
    isEmailValid: false,
  };
  emailAddressArray: any = [];
  userProfile?: UserModel;
  export_address!: any;
  isLoading!: boolean;
  is_order: boolean = false;
  submitted: boolean = false;
  emailForm!: UntypedFormGroup;
  emails!: UntypedFormArray;

  constructor(
    public ngbActiveModal: NgbActiveModal,
    private authenticationService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private quoteOrderService: QuoteOrderSampleService,
    private helperService: HelperService,
    private alertService: AlertService,
    public router: Router,
    public subscribersService: SubscribersService,
    public fb: UntypedFormBuilder,
    @Inject(PLATFORM_ID) private platformId: any,
    private debugLogger: DebugLoggerService,
  ) {
    const urlSplit = router.url.split('/');
    if (urlSplit[1] == 'orders') {
      this.is_order = true;
    }
    subscribersService._profileModel.subscribe((value) => {
      this.userProfile = value;
    });
  }

  ngOnInit(): void {
    if (this.quoteModel.type == 'O') {
      this.is_order = true;
    }
    this.export_address = 1;
    let emailObject = {
      email: '',
      isEmpty: false,
      isEmailValid: false,
    };
    this.emailAddressArray.push(emailObject);
    this.emailAddressObject.email = this.userProfile?.email;
    this.initForm();
    // The modal's inputs (quoteModel, etc.) are set imperatively right after open(), so under
    // this console's OnPush-by-default patch a freshly opened modal can stay unpainted when it
    // is launched from an OnPush parent (e.g. the preview-detail modal) — it looks like it
    // never opened. Force the initial paint, matching the other modals in this app
    // (ConfirmModalComponent / StatusChangeConfirmationComponent).
    this.changeDetectorRef.detectChanges();
  }

  get f() {
    return this.emailForm.controls;
  }

  get fe() {
    return <UntypedFormArray>this.emailForm.get('emails');
  }

  initForm() {
    this.emailForm = this.fb.group({
      email_type: new UntypedFormControl('1', [Validators.required]),
      oldEmail: new UntypedFormControl(this.quoteModel.quoteOwner?.email ? this.quoteModel.quoteOwner?.email : '', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
      emails: new UntypedFormArray([], [])
    })

  }

  /** An added address row. `checked` is its tick, as console-v2 gives every row:
   *  ticked by default; unticked, the address is not sent and not validated. */
  createEmail(): UntypedFormGroup {
    return this.fb.group({
      checked: new UntypedFormControl(true),
      email: new UntypedFormControl('', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
    });
  }

  emailsArray() {
    return this.emailForm.get('emails') as UntypedFormArray;
  }

  canAddEmail(): boolean {
    return this.emailsArray().controls.length < MAX_ADDED_EMAILS;
  }

  addNewEmail() {
    if (!this.canAddEmail()) {
      return;
    }
    this.emails = this.emailForm.get('emails') as UntypedFormArray
    this.emails.push(this.createEmail());
    this.changeDetectorRef.detectChanges()
  }

  /** An added row's tick - the same switch onFirstCheck() is for the contact's row. */
  onAddedCheck(index: number, event: any) {
    this.hasFormError = false;
    const email = this.fe.at(index)?.get('email');
    email?.setValidators(event.target.checked ? [Validators.required, Validators.pattern(EMAIL_PATTERN)] : []);
    email?.updateValueAndValidity();
  }

  /** Ticked added rows - the only ones exported. */
  private checkedAddedEmails(): any[] {
    return this.fe.value.filter((row: any) => row.checked);
  }

  onFirstCheck(event: any) {
    this.hasFormError = false;
    if (event.target.checked) {
      this.emailForm.get('oldEmail')?.setValidators([Validators.required, Validators.pattern(EMAIL_PATTERN)]);
      this.emailForm.get('oldEmail')?.updateValueAndValidity()
    } else {
      this.emailForm.get('oldEmail')?.setValidators([]);
      this.emailForm.get('oldEmail')?.updateValueAndValidity()
    }
  }

  exportQuote() {
    this.submitted = true;
    if (this.emailForm.controls.email_type.value || this.checkedAddedEmails().length > 0) {
      if (this.emailForm.valid && this.fe.valid) {
        this.isLoading = true;
        let formData = new FormData();
        // Gate on the actual primary-email field value (oldEmail), not on emailAddressObject
        // (which is seeded from the logged-in user's profile email). Previously, if that profile
        // email was empty/not loaded, the recipient the user actually sees/enters was never
        // appended, so emailArray stayed empty and the export closed silently with no API call.
        const primaryEmail = this.emailForm.controls.oldEmail.value;
        if (this.emailForm.controls.email_type.value && primaryEmail !== undefined && primaryEmail !== null && primaryEmail !== '') {
          formData.append('email[]', primaryEmail);
        }
        if (this.checkedAddedEmails().length > 0) {
          this.checkedAddedEmails().forEach((email: any) => {
            if (email.email != undefined && email.email != null && email.email != '') {
              formData.append('email[]', email.email);
            }
          })
        }
        var emailArray = formData.getAll('email[]')
        if (emailArray && emailArray.length > 0) {
          this.handlePDFExport(formData)
        } else {
          this.isLoading = false;
          this.ngbActiveModal.close(false);
        }
      }
    } else {
      this.alertService.error(Constants.EMAIL_IS_REQUIRED_FOR_PDF_EXPORT);
    }
  }

  handlePDFExport(formData: FormData) {
    if (this.isConsumableSummaryExportPopupOn) {
      this.sendPDFExportRequest(this.quoteOrderService.quoteExportPDF(this.quoteModel.id, formData), this.is_order ? Constants.SUCCESS_MESSAGE_ON_EXPORT_PDF_INVOICE : Constants.SUCCESS_MESSAGE_ON_EXPORT_PDF_QUOTE);
    } else {
      this.sendPDFExportRequest(this.quoteOrderService.exportConsumableSummaryPDF(this.quoteModel.id, formData), Constants.SUCCESS_MESSAGE_ON_EXPORT_PDF_CONSUMABLE_SUMMARY);
    }
  }

  sendPDFExportRequest(requestObservable: Observable<any>, successMessage: string) {
    requestObservable.subscribe(
      (response: any) => {
        if (successMessage) {
          this.alertService.success(successMessage);
        }
        this.ngbActiveModal.close(true);
        this.isLoading = false;
      },
      (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.ngbActiveModal.close(false);
        this.helperService.handleError(error);
      }
    );
  }

  async copyQuoteInvoiceLink(isDisplaySuccessMessage?: boolean) {
    if (this.quoteModel.export_id == null) {
      this.isLoading = true;
      const requestParamModel = new RequestParamModel();
      delete requestParamModel.pageSize;
      await this.quoteOrderService
        .generateExportId(this.quoteModel.id, requestParamModel)
        .subscribe(
          (response: any) => {
            this.quoteModel.export_id = response.export_id;
            this.isLoading = false;
            this.copyLink(isDisplaySuccessMessage);
          },
          (error: HttpErrorResponse) => {
            this.isLoading = false;
            this.helperService.handleError(error);
          }
        );
    } else {
      this.copyLink(isDisplaySuccessMessage);
    }
  }

  copyLink(isDisplaySuccessMessage: boolean) {
    var exportQuoteInvoiceURL = Constants.EXPORT_QUOTE_INVOICE_URL + '/' + this.quoteModel?.export_id;
    let selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = decodeURIComponent(exportQuoteInvoiceURL);
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    if (isDisplaySuccessMessage) {
      this.alertService.success(Constants.QUOTE_INVOICE_LINK_COPIED);
    }
  }

  downloadPDF() {
    if (this.quoteModel.id !== null && this.quoteModel.id !== undefined && this.quoteModel.id > 0) {
      this.isLoading = true;
      let params: any = {};
      params.id = this.quoteModel.id;
      params.type = this.typeForDownload;

      // const url = this.quoteOrderService.downloadQuoteOrder(params);
      // if (isPlatformBrowser(this.platformId))
      //   window.location.assign(url);
      // this.isLoading = false;


      this.quoteOrderService.downloadQuoteOrder(params).subscribe((data: any) => {
        // this.debugLogger.log('data==== ', data)
        if (data !== null && data !== undefined && data !== "") {
          let json = {
            file_name: data
          };
          const url = this.quoteOrderService.downloadQuoteOrderNew(json);
          if (isPlatformBrowser(this.platformId))
            window.location.assign(url);
          this.isLoading = false;
        }
        this.isLoading = false;
      }, (error: HttpErrorResponse) => {
        // if (error.message) {
        //   this.router.navigate(['/']);
        // }
        this.isLoading = false;
      });
    }
  }

  getExportButtonName() {
    if (this.isConsumableSummaryExportPopupOn) {
      if (this.is_order) {
        return 'Export Invoice';
      } else {
        return 'Export Quote';
      }
    } else {
      return 'Export Consumable Summary'
    }
  }

}
